/**
 * @module Questions
 * @description Questions page component for managing question categories, subcategories, and questions in the mental health portal.
 * Provides UI for creating, editing, and deleting questions with category and subcategory organization.
 */

import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  CircularProgress,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ContainerPage from '../../components/ContainerPage/ContainerPage';
import Modal from '../../components/Resources/Modal/Modal';

import useQuestions from '../../hooks/questions/useQuestions';

import {
  ButtonStyled,
  CategoryItem,
  CategoryList,
  CategorySubItem,
  ContainerCategoryItem,
  HeaderContainer,
  HeaderSection,
  HeaderTitle,
  Main,
  ModalHeader,
  PageTitle,
  PageTitleButton,
  QuestionDeleteSection,
  QuestionFormSection,
  QuestionItem,
  QuestionList,
  SectionCategory,
  SectionContentQuestion,
  SectionQuestion,
} from './Questions.Styles';

/**
 * @function Questions
 * @description Component for managing question categories, subcategories, and questions
 * @returns {JSX.Element} Questions page component
 */
const Questions = () => {
  const { t } = useTranslation();

  /**
   * @description Destructuring values and methods from useQuestions hook
   */
  const {
    isLoading,
    selectQuestion,
    selectedQuestion,
    handleNewQuestionForm,
    filteredQuestions,
    isOpenQuestionForm,
    handleEditQuestionForm,
    formQuestionData,
    updateFormQuestionField,
    handleSaveQuestionForm,
    handleCloseQuestionForm,
    isOpenDeleteQuestionModal,
    handleOpenDeleteQuestionModal,
    handleCloseDeleteQuestionModal,
    handlerDeleteQuestionModal,
    categories,
    selectedCategory,
    selectCategory,
    selectedSubcategory,
    selectSubcategory,
    handleUpdateQuestionForm,
    formQuestionDataOriginal,
    showAlert,
    handleCloseAlert,
    alertMessage,
    typeAlert,
    errorsFormQuestion,
  } = useQuestions();

  /**
   * @function truncateDescription
   * @description Truncates a description text to a specified length and adds ellipsis
   * @param {string} description - The text to truncate
   * @param {number} [maxLength=25] - Maximum length before truncation
   * @returns {string} Truncated text with ellipsis if needed
   */
  const truncateDescription = (description, maxLength = 25) =>
    description && description.length > maxLength
      ? description.substring(0, maxLength) + '...'
      : description;

  /**
   * @constant {boolean} hasCategories
   * @description Checks if there are any categories available
   */
  const hasCategories = !!categories?.length;

  /**
   * @constant {boolean} hasFilteredQuestions
   * @description Checks if there are any filtered questions available
   */
  const hasFilteredQuestions = !!filteredQuestions?.length;

  /**
   * @constant {boolean} hasSelectedCategory
   * @description Checks if a category is currently selected
   */
  const hasSelectedCategory = !!selectedCategory?.id;

  return (
    <ContainerPage>
      <PageTitle>
        {isOpenQuestionForm ? (
          <>
            <PageTitleButton onClick={handleCloseQuestionForm}>
              {'<'}
            </PageTitleButton>{' '}
            {formQuestionData.id ? t('edit question') : t('new question')}
          </>
        ) : (
          t('questions')
        )}
      </PageTitle>

      {/* Alert notification */}
      <Snackbar
        open={showAlert}
        autoHideDuration={10000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={typeAlert}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      <Main
        isLoading={isLoading}
        hasCategories={hasCategories}
        hasFilteredQuestions={hasFilteredQuestions}
      >
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            {/* HEADER */}
            <HeaderContainer>
              <HeaderSection align="center" flexDirection="column">
                {!isOpenQuestionForm && (
                  <>
                    <ButtonStyled
                      onClick={handleNewQuestionForm}
                      disabled={!hasCategories}
                    >
                      {t('create question')}
                    </ButtonStyled>
                  </>
                )}
              </HeaderSection>

              <HeaderSection align="space-between">
                {selectedCategory && (
                  <>
                    <HeaderTitle>
                      {t(
                        `${selectedSubcategory?.id ? selectedSubcategory.nameSubCategorie : selectedCategory.name}`
                      )}
                    </HeaderTitle>
                  </>
                )}
              </HeaderSection>

              <HeaderSection align="space-between">
                {selectedQuestion && (
                  <>
                    <HeaderTitle>{selectedQuestion.title}</HeaderTitle>
                  </>
                )}
              </HeaderSection>
            </HeaderContainer>

            {hasCategories ? (
              <>
                {/* QUESTION FORM */}
                {isOpenQuestionForm ? (
                  <QuestionFormSection>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();

                        if (formQuestionData.id) {
                          handleUpdateQuestionForm(
                            formQuestionData,
                            formQuestionDataOriginal
                          );
                          return;
                        }

                        handleSaveQuestionForm(formQuestionData);
                      }}
                    >
                      <select
                        value={formQuestionData.category_id || ''}
                        onChange={(e) => {
                          updateFormQuestionField(
                            'category_id',
                            e.target.value
                          );

                          if (e.target.value === 'weekly') {
                            updateFormQuestionField('subcategory_id', null);
                          }
                        }}
                        className={
                          errorsFormQuestion.category_id ? 'error' : ''
                        }
                      >
                        <option value="" disabled>
                          {t('Category')}
                        </option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {t(`${category.name}`)}
                          </option>
                        ))}
                      </select>

                      {categories.find(
                        (category) =>
                          category.id === formQuestionData.category_id &&
                          category.subcategories.length > 0
                      ) && (
                        <select
                          value={formQuestionData.subcategory_id || ''}
                          onChange={(e) =>
                            updateFormQuestionField(
                              'subcategory_id',
                              e.target.value
                            )
                          }
                          className={
                            errorsFormQuestion.subcategory_id ? 'error' : ''
                          }
                        >
                          <option value="" disabled>
                            {t('subcategory')}
                          </option>
                          {categories.map((category) =>
                            category.subcategories.map((subcategory) => (
                              <option
                                key={subcategory.id}
                                value={subcategory.id}
                              >
                                {t(`${subcategory.nameSubCategorie}`)}
                              </option>
                            ))
                          )}
                        </select>
                      )}

                      <input
                        type="text"
                        placeholder={t('question name')}
                        value={formQuestionData.title || ''}
                        onChange={(e) =>
                          updateFormQuestionField('title', e.target.value)
                        }
                        className={errorsFormQuestion.title ? 'error' : ''}
                      />

                      <textarea
                        placeholder={t('content question')}
                        value={formQuestionData.question || ''}
                        onChange={(e) =>
                          updateFormQuestionField('question', e.target.value)
                        }
                        className={errorsFormQuestion.question ? 'error' : ''}
                      />

                      <div className="buttons-area">
                        <ButtonStyled
                          type="button"
                          onClick={handleCloseQuestionForm}
                        >
                          {t('Cancel')}
                        </ButtonStyled>
                        <ButtonStyled type="submit">{t('Save')}</ButtonStyled>
                      </div>
                    </form>
                  </QuestionFormSection>
                ) : (
                  <>
                    {/* CATEGORY AND SUBCATEGORY LIST */}
                    <SectionCategory>
                      <CategoryList>
                        {categories.map((category) => {
                          const hasSubcategories =
                            category?.subcategories?.length > 0;

                          return (
                            <ContainerCategoryItem key={'0' + category.id}>
                              <CategoryItem
                                key={'1' + category.id}
                                hasSubcategories={hasSubcategories}
                                isActive={
                                  selectedCategory?.id === category.id &&
                                  !hasSubcategories
                                }
                                onClick={() => {
                                  if (hasSubcategories) return;
                                  selectCategory(category.id);
                                }}
                              >
                                <span>{t(`${category.name}`)}</span>
                                <span>{category.count}</span>
                              </CategoryItem>
                              {category.subcategories?.length > 0 &&
                                category.subcategories.map((subcategory) => (
                                  <CategorySubItem
                                    key={'2' + subcategory.id}
                                    isActive={
                                      selectedSubcategory?.id === subcategory.id
                                    }
                                    onClick={() => {
                                      selectSubcategory(subcategory.id);
                                    }}
                                  >
                                    <span>
                                      {t(`${subcategory.nameSubCategorie}`)}
                                    </span>
                                    <span>{subcategory.count}</span>
                                  </CategorySubItem>
                                ))}
                            </ContainerCategoryItem>
                          );
                        })}
                      </CategoryList>
                    </SectionCategory>

                    {hasSelectedCategory || hasSelectedCategory ? (
                      hasFilteredQuestions ? (
                        <>
                          {/* QUESTION LIST */}
                          <SectionQuestion>
                            <QuestionList>
                              {filteredQuestions.map((question) => (
                                <QuestionItem
                                  key={question.id}
                                  isActive={
                                    selectedQuestion?.id === question.id
                                  }
                                  onClick={() => selectQuestion(question.id)}
                                >
                                  <div className="info-card">
                                    <span>{question.title}</span>
                                    <p>
                                      {truncateDescription(
                                        question.description
                                      )}
                                    </p>
                                  </div>
                                  <div className="buttons-card">
                                    <button
                                      onClick={() =>
                                        handleEditQuestionForm(question.id)
                                      }
                                    >
                                      <EditIcon
                                        htmlColor="rgba(0, 0, 0, 0.6)"
                                        fontSize="small"
                                      />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleOpenDeleteQuestionModal(
                                          question.id
                                        )
                                      }
                                    >
                                      <DeleteIcon
                                        htmlColor="rgba(0, 0, 0, 0.6)"
                                        fontSize="small"
                                      />
                                    </button>
                                  </div>
                                </QuestionItem>
                              ))}
                            </QuestionList>
                          </SectionQuestion>

                          {/* QUESTION CONTENT */}
                          <SectionContentQuestion>
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                              {selectedQuestion?.question}
                            </Typography>
                          </SectionContentQuestion>

                          {/* QUESTION DELETE MODAL */}
                          <Modal show={isOpenDeleteQuestionModal}>
                            <ModalHeader>
                              <span className="title-modal">
                                {t('delete question')}
                              </span>
                              <IconButton
                                onClick={handleCloseDeleteQuestionModal}
                              >
                                <CloseIcon />
                              </IconButton>
                            </ModalHeader>
                            <QuestionDeleteSection>
                              <div className="text-alert">
                                <p>{t('alert-delete-message')}</p>
                              </div>
                              <div className="buttons-area">
                                <ButtonStyled
                                  onClick={handleCloseDeleteQuestionModal}
                                >
                                  {t('Cancel')}
                                </ButtonStyled>
                                <ButtonStyled
                                  onClick={() =>
                                    handlerDeleteQuestionModal(selectedQuestion)
                                  }
                                >
                                  {t('Confirm')}
                                </ButtonStyled>
                              </div>
                            </QuestionDeleteSection>
                          </Modal>
                        </>
                      ) : (
                        <p> {t('no messages available')}</p>
                      )
                    ) : (
                      <p> {t('select category')}</p>
                    )}
                  </>
                )}
              </>
            ) : (
              <p>{t('no categories available')}</p>
            )}
          </>
        )}
      </Main>
    </ContainerPage>
  );
};

export default Questions;
